// src/components/apps/SharePointHR/SharePointUploadDialog.jsx
import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, LinearProgress, Typography, Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadFile } from "../../../services/sharePointService";

/**
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   parentFolderId: string|null (current folder ID, null for root)
 *   onUploadComplete: () => void (callback to refresh the file list)
 */
const SharePointUploadDialog = ({ open, onClose, parentFolderId, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setError(null);
    setProgress(0);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setProgress(0);
    try {
      await uploadFile(parentFolderId, file, setProgress);
      setFile(null);
      setProgress(100);
      onUploadComplete();
      onClose();
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setError(null);
      setProgress(0);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload File to HR Documents</DialogTitle>
      <DialogContent>
        <input
          type="file"
          onChange={handleFileChange}
          disabled={uploading}
          style={{ marginTop: "8px", marginBottom: "16px" }}
        />
        {file && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
          </Typography>
        )}
        {uploading && (
          <LinearProgress variant="determinate" value={progress} sx={{ mt: 2, mb: 1 }} />
        )}
        {uploading && (
          <Typography variant="body2" color="text.secondary">
            Uploading... {progress}%
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          startIcon={<CloudUploadIcon />}
          disabled={!file || uploading}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePointUploadDialog;
